import React, {Component} from 'react';
import {SchemaUtils} from "../util/SchemaUtils";
import {Component as AppComponent} from "../enum/Component";
import moment from "moment/moment";
import {DateUtils} from "../util/DateUtils";
import {StringUtils} from "../util/StringUtils";
import { ReviewBox, ReviewChipItem, ReviewList, ReviewListItem, ReviewListLabel, ReviewListText, ReviewTitle } from '../styles/jss/global';
import {ValidationUtil} from "../util/ValidationUtil";

interface IReviewDataProperties {
    uiSchemas: any;
    dataSchema: any;
    appData: any;
    showPages: Array<string>;
    uniqueIds?: Array<{key: string, value: string}>;
    postSubmission: boolean;
    appTemplate: any;
}

class ReviewData extends Component<IReviewDataProperties, {}> {

    state = {
        reload: false
    };

    refreshPage = () => {
        this.setState(
            {reload: true},
            () => this.setState({reload: false})
        )
    }

    handleSignatureChange = (path: string, file: string) => {
        const pathParts = path.split(".");
        this.props.appData[pathParts[0]][pathParts[1]][pathParts[2]] = file;
        this.refreshPage();
    }

    getReadableColumnName = (key: string) => {
        const parts = key.split('_#_');
        const words = parts.slice(0, -1); // Remove the numeric index
        return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    
    getCellValue = (cellData: any) => {
        if (!cellData) return '';

        const valueKey = Object.keys(cellData)[0];
        const value = cellData[valueKey];

        // Check if this is a date field
        if (valueKey === 'date' && value) {
            const momentValue = DateUtils.getMomentValue(value);
            if (momentValue && momentValue.isValid()) {
                // Get the locale's short date format (L) which is like MM/DD/YYYY or DD/MM/YYYY
                return momentValue.format('L');
            }
        }

        return value ?? '';
    }



    renderColumnsTable = (appData: any) => {
        if (!appData || !Array.isArray(appData) || appData.length === 0) {
            return <span style={{color: '#6c757d', fontStyle: 'italic'}}>No data</span>;
        }

        const primaryColor = this.props.appTemplate?.colors?.primary ?? '#607d8b';

        // Create lighter/darker variants for better contrast
        const headerBgColor = primaryColor;
        const headerTextColor = '#ffffff'; // White text for better contrast on primary
        const borderColor = this.lightenColor(primaryColor, 30); // Lightened border
        const evenRowBgColor = '#ffffff';
        const oddRowBgColor = this.lightenColor(primaryColor, 85); // Very light primary tint

        // Extract column headers from the first object's keys
        const columns = Object.keys(appData[0]).map(key => ({
            key: key,
            name: this.getReadableColumnName(key)
        }));

        return (
            <div style={{
                overflowX: 'auto',
                marginTop: '10px',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px',
                    minWidth: '600px',
                    fontFamily: 'inherit'
                }}>
                    {/* Table Headers */}
                    <thead>
                    <tr style={{ backgroundColor: headerBgColor }}>
                        {columns.map(column => (
                            <th key={column.key} style={{
                                padding: '12px 8px',
                                border: `1px solid ${borderColor}`,
                                fontWeight: '600',
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                color: headerTextColor,
                                fontSize: '14px'
                            }}>
                                {column.name}
                            </th>
                        ))}
                    </tr>
                    </thead>

                    {/* Table Body */}
                    <tbody>
                    {appData.map((row: any, rowIndex: number) => (
                        <tr key={rowIndex} style={{
                            backgroundColor: rowIndex % 2 === 0 ? evenRowBgColor : oddRowBgColor,
                            transition: 'background-color 0.2s ease'
                        }}>
                            {columns.map(column => (
                                <td key={column.key} style={{
                                    padding: '12px 8px',
                                    border: `1px solid ${borderColor}`,
                                    verticalAlign: 'top',
                                    whiteSpace: 'nowrap',
                                    color: '#333333' // Dark text for readability
                                }}>
                                    {this.getCellValue(row[column.key])}
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        );
    }

    // Helper function to lighten/darken colors
    lightenColor = (color: string, percent: number) => {
        const num = parseInt(color.replace("#", ""), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) + amt;
        const G = (num >> 8 & 0x00FF) + amt;
        const B = (num & 0x0000FF) + amt;
        return "#" + (
            0x1000000 +
            (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)
        ).toString(16).slice(1);
    }

    render() {
        return Object.keys(this.props.dataSchema.properties).map(((page: string, pageIndex: number) => {
            let pageHeading = this.props.dataSchema.properties[page].title ?
                this.props.dataSchema.properties[page].title :
                StringUtils.convertKeyToLabel(page, pageIndex);
            if(this.props.showPages.indexOf(pageHeading) === -1 && this.props.showPages.length > 0){
                return "";
            }

            return (
                <ReviewBox key={pageIndex}>
                    <ReviewTitle component={'h3'}>{pageHeading}</ReviewTitle>

                    <ReviewList>
                        {Object.keys(this.props.appData[page]).map((component: string, componentIndex: number) => {
                            let uiSchema = SchemaUtils.getRelevantUiSchema(this.props.uiSchemas,
                                this.props.appData, page, component);
                            let enabled = SchemaUtils.isComponentEnabled(uiSchema, this.props.appData);
                            let visible = SchemaUtils.isComponentVisible(uiSchema, this.props.appData, this.props.postSubmission);
                            const componentType = Object.keys(this.props.appData[page][component])[0];
                            if (uiSchema.label.trim().toLowerCase() === AppComponent.Content ||
                                componentType === AppComponent.Summary ||
                                !visible) {
                                return "";
                            }

                            let pageAppData = this.props.appData[page];
                            let componentAppData = pageAppData[component];
                            const uniqueKey = `${page}${StringUtils.spaceSeparatorChars}${component}`;
                            if (Object.keys(componentAppData).includes(AppComponent.Unique)) {
                                let uniqueValue = '';
                                if (this.props.uniqueIds) {
                                    const keyValuePair = this.props.uniqueIds.find(kvp => kvp.key === uniqueKey);
                                    if (keyValuePair) {
                                        uniqueValue = keyValuePair.value;
                                    }
                                }
                                if (ValidationUtil.isEmptyValue(uniqueValue)) {
                                    return null;
                                }
                            }

                            return (
                                <ReviewListItem key={componentIndex}>
                                    <ReviewListText>
                                        <ReviewListLabel component="span"
                                                         className={`${!enabled ? 'reviewListLabelDisable' : ''}`}>
                                            {uiSchema.label} : </ReviewListLabel>
                                        {
                                            Object.keys(componentAppData).map((type: string, dataIndex: number) => {
                                                let appData = componentAppData[type];

                                                switch (type) {
                                                    case AppComponent.FileUpload: {
                                                        if (ValidationUtil.isEmptyValue(appData)) {
                                                            return "";
                                                        }
                                                        try {
                                                            return (
                                                                appData.map((appDataItem: any) => {
                                                                    return (<ReviewChipItem key={appDataItem.id}>
                                                                        {appDataItem.name}</ReviewChipItem>);
                                                                })
                                                            );
                                                        } catch (e) {
                                                        }
                                                        return "";
                                                    }
                                                    case AppComponent.Date: {
                                                        if (ValidationUtil.isEmptyValue(appData)) {
                                                            return "";
                                                        }
                                                        return (
                                                            moment(appData).format(DateUtils.getLongDateFormat())
                                                        );
                                                    }
                                                    case AppComponent.Content: {
                                                        if (ValidationUtil.isEmptyValue(appData)) {
                                                            return "";
                                                        }
                                                        return "";
                                                    }
                                                    case AppComponent.Time: {
                                                        if (ValidationUtil.isEmptyValue(appData)) {
                                                            return "";
                                                        }
                                                        const isAmPm = this.props.dataSchema.properties[page].properties[component].properties.time.ampm;
                                                        const parseFormat = isAmPm ? "hh:mm A" : "HH:mm";
                                                        const formattedTime = moment(appData, parseFormat).format(DateUtils.getTimeFormat(isAmPm));
                                                        return (
                                                            formattedTime
                                                        );
                                                    }
                                                    case AppComponent.Checkbox: {
                                                        if (ValidationUtil.isEmptyValue(appData)) {
                                                            return "";
                                                        } else if (Array.isArray(appData) && appData.length > 0) {
                                                            return appData.map((appDataItem: any) => {
                                                                return (
                                                                    <ReviewChipItem key={appDataItem}>
                                                                        {appDataItem}
                                                                    </ReviewChipItem>
                                                                )
                                                            });
                                                        }
                                                        return "";
                                                    }
                                                    case AppComponent.Unique: {
                                                        // Since we early-returned null if empty, just return the value here
                                                        if (this.props.uniqueIds) {
                                                            const keyValuePair =
                                                                this.props.uniqueIds.find(kvp => kvp.key === uniqueKey);
                                                            if (keyValuePair) {
                                                                return keyValuePair.value;
                                                            }
                                                        }
                                                        return "";
                                                    }
                                                    case AppComponent.Signature: {
                                                        if (!ValidationUtil.isEmptyValue(appData)) {
                                                            return (
                                                                <img key={`${component}`}
                                                                     src={appData}
                                                                     width={'100%'}
                                                                     height={'auto'}
                                                                     alt="Signature"/>)
                                                        }
                                                        return "";
                                                    }
                                                    case AppComponent.Rating: {
                                                        const maximum = this.props.dataSchema.properties[page].properties[component].properties.rating?.maximum || 5;
                                                        const ratingValue = Math.min(Math.max(parseInt(appData) || 0, 0), maximum);

                                                        // Generate stars
                                                        const filledStars = '★'.repeat(ratingValue);
                                                        const emptyStars = '☆'.repeat(maximum - ratingValue);

                                                        return (
                                                            <span key={dataIndex} style={{
                                                                fontSize: '20px',
                                                                letterSpacing: '2px',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                <span style={{ color: '#FFD700' }}>{filledStars}</span>
                                                                <span style={{ color: '#CCCCCC' }}>{emptyStars}</span>
                                                            </span>
                                                        );
                                                    }
                                                    case AppComponent.Columns: {
                                                        if (!ValidationUtil.isEmptyValue(appData)) {
                                                            return this.renderColumnsTable(appData);
                                                        }
                                                        return "";
                                                    }
                                                    default: {
                                                        if (ValidationUtil.isEmptyValue(appData)) {
                                                            return "";
                                                        }
                                                        return (
                                                            appData
                                                        )
                                                    }
                                                }
                                            })
                                        }
                                    </ReviewListText>
                                </ReviewListItem>
                            );
                        })}
                    </ReviewList>
                </ReviewBox>
            );
        }));
    }
}

export default ReviewData;
